import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
    Leaf, ArrowLeft, Trophy, Coins, Timer, Crosshair, Star, X, Play
} from '@phosphor-icons/react';

const DIFFICULTIES = {
    easy: { cols: 4, rows: 3, pairs: 6, label: 'Easy' },
    medium: { cols: 4, rows: 4, pairs: 8, label: 'Medium' },
    hard: { cols: 5, rows: 4, pairs: 10, label: 'Hard' }
};

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export default function MemoryMatchGame() {
    const { api } = useAuth();
    const [animals, setAnimals] = useState([]);
    const [difficulty, setDifficulty] = useState(null); // null = not started
    const [cards, setCards] = useState([]);
    const [flipped, setFlipped] = useState([]);
    const [matched, setMatched] = useState([]);
    const [moves, setMoves] = useState(0);
    const [time, setTime] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [matchedAnimal, setMatchedAnimal] = useState(null); // animal info modal
    const [result, setResult] = useState(null); // completion result
    const [submitting, setSubmitting] = useState(false);
    const timerRef = useRef(null);
    const lockRef = useRef(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api().get('/games/memory-animals');
                setAnimals(res.data);
            } catch (err) {
                console.error('Failed to load animals');
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startGame = useCallback((diff) => {
        const config = DIFFICULTIES[diff];
        const selected = shuffle(animals).slice(0, config.pairs);
        const pairs = shuffle([...selected, ...selected].map((a, i) => ({
            ...a,
            uid: `${a.id}-${i}`
        })));
        setDifficulty(diff);
        setCards(pairs);
        setFlipped([]);
        setMatched([]);
        setMoves(0);
        setTime(0);
        setGameOver(false);
        setResult(null);
        setMatchedAnimal(null);

        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => setTime(t => t + 1), 1000);
    }, [animals]);

    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    const handleFlip = useCallback((index) => {
        if (lockRef.current) return;
        if (flipped.includes(index)) return;
        if (matched.includes(cards[index]?.id)) return;

        const newFlipped = [...flipped, index];
        setFlipped(newFlipped);

        if (newFlipped.length === 2) {
            setMoves(m => m + 1);
            lockRef.current = true;

            const [first, second] = newFlipped;
            if (cards[first].id === cards[second].id) {
                // Match found!
                const animal = cards[first];
                const newMatched = [...matched, animal.id];
                setMatched(newMatched);

                setTimeout(() => {
                    setFlipped([]);
                    setMatchedAnimal(animal);
                    lockRef.current = false;

                    // Check if game over
                    if (newMatched.length === DIFFICULTIES[difficulty].pairs) {
                        clearInterval(timerRef.current);
                        // Don't set game over yet, wait for modal close
                    }
                }, 600);
            } else {
                // No match — flip back
                setTimeout(() => {
                    setFlipped([]);
                    lockRef.current = false;
                }, 1000);
            }
        }
    }, [flipped, matched, cards, difficulty]);

    const closeAnimalModal = () => {
        setMatchedAnimal(null);
        if (matched.length === DIFFICULTIES[difficulty]?.pairs) {
            setGameOver(true);
            completeGame();
        }
    };

    const completeGame = async () => {
        setSubmitting(true);
        try {
            const res = await api().post('/games/complete', {
                game_id: 'memory_match',
                score: 100,
                moves: moves,
                time_seconds: time,
                difficulty: difficulty
            });
            setResult(res.data);
        } catch (err) {
            console.error('Failed to submit game result');
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    // ==================== DIFFICULTY SELECT ====================
    if (!difficulty) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2" data-testid="back-games">
                                <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                    <Leaf weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Memory Match</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5">
                                <ArrowLeft weight="bold" size={16} /> Games
                            </Link>
                        </div>
                    </div>
                </nav>

                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">🧠</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Memory Match</h1>
                        <p className="text-[#475569] mb-8">Flip cards to find matching Australian animals! Each match reveals a fun fact and video.</p>

                        <p className="font-fredoka font-bold text-[#1e293b] mb-4">Choose Difficulty</p>
                        <div className="grid sm:grid-cols-3 gap-4">
                            {Object.entries(DIFFICULTIES).map(([key, val]) => (
                                <motion.button
                                    key={key}
                                    whileHover={{ y: -4, x: -4 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    onClick={() => startGame(key)}
                                    className="brutalist-card p-5 text-center hover:shadow-[8px_8px_0px_0px_#1e293b] transition-shadow"
                                    data-testid={`difficulty-${key}`}
                                    disabled={animals.length < val.pairs}
                                >
                                    <p className="font-fredoka text-xl font-bold text-[#1e293b]">{val.label}</p>
                                    <p className="text-sm text-[#475569] mt-1">{val.cols}x{val.rows} grid</p>
                                    <p className="text-sm text-[#475569]">{val.pairs} pairs</p>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    // ==================== GAME OVER ====================
    if (gameOver) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="brutalist-card p-8 max-w-md w-full text-center bg-white" data-testid="game-complete">
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Well Done!</h2>
                    <p className="text-[#475569] mb-6">You found all the pairs!</p>

                    <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="p-3 bg-[#f8fafc] rounded-xl">
                            <Crosshair weight="duotone" size={20} className="text-[#38bdf8] mx-auto" />
                            <p className="font-fredoka text-xl font-bold text-[#1e293b]">{moves}</p>
                            <p className="text-xs text-[#94a3b8]">Moves</p>
                        </div>
                        <div className="p-3 bg-[#f8fafc] rounded-xl">
                            <Timer weight="duotone" size={20} className="text-[#a855f7] mx-auto" />
                            <p className="font-fredoka text-xl font-bold text-[#1e293b]">{formatTime(time)}</p>
                            <p className="text-xs text-[#94a3b8]">Time</p>
                        </div>
                        <div className="p-3 bg-[#fef3c7] rounded-xl">
                            <Coins weight="duotone" size={20} className="text-[#f59e0b] mx-auto" />
                            <p className="font-fredoka text-xl font-bold text-[#1e293b]">
                                {submitting ? '...' : result ? `+${result.tokens_earned}` : '...'}
                            </p>
                            <p className="text-xs text-[#94a3b8]">Tokens</p>
                        </div>
                    </div>

                    {result?.daily_limit_reached && (
                        <p className="text-sm text-[#f59e0b] font-semibold mb-4">Daily token limit reached! Come back tomorrow.</p>
                    )}

                    <div className="flex gap-3">
                        <button onClick={() => startGame(difficulty)}
                            className="brutalist-btn flex-1 bg-[#22c55e] text-white flex items-center justify-center gap-2"
                            data-testid="play-again">
                            <Play weight="bold" size={18} /> Play Again
                        </button>
                        <Link to="/games" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center" data-testid="back-to-games">
                            Games Hub
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    // ==================== GAME BOARD ====================
    const config = DIFFICULTIES[difficulty];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)]">
            {/* Top bar */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b] px-4 py-3">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/games" className="flex items-center gap-2 text-[#475569] hover:text-[#1e293b] font-semibold" data-testid="back-to-games-link">
                        <ArrowLeft weight="bold" size={20} /> Games
                    </Link>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-sm font-semibold">
                            <Crosshair weight="bold" size={18} className="text-[#38bdf8]" />
                            <span data-testid="move-count">{moves} moves</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm font-semibold">
                            <Timer weight="bold" size={18} className="text-[#a855f7]" />
                            <span data-testid="timer">{formatTime(time)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm font-semibold">
                            <Star weight="bold" size={18} className="text-[#f59e0b]" />
                            <span data-testid="pairs-found">{matched.length}/{config.pairs}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Card grid */}
            <div className="max-w-4xl mx-auto px-4 pt-20 pb-8 flex items-center justify-center min-h-screen">
                <div className="grid gap-3"
                    style={{ gridTemplateColumns: `repeat(${config.cols}, minmax(0, 1fr))`, maxWidth: config.cols * 110 }}>
                    {cards.map((card, index) => {
                        const isFlipped = flipped.includes(index);
                        const isMatched = matched.includes(card.id);

                        return (
                            <motion.div
                                key={card.uid}
                                className="relative cursor-pointer"
                                style={{ perspective: 600, width: 90, height: 110 }}
                                onClick={() => handleFlip(index)}
                                data-testid={`card-${index}`}
                            >
                                <motion.div
                                    className="absolute inset-0 rounded-xl border-3 border-[#1e293b]"
                                    animate={{ rotateY: isFlipped || isMatched ? 180 : 0 }}
                                    transition={{ duration: 0.4 }}
                                    style={{ transformStyle: 'preserve-3d' }}
                                >
                                    {/* Back of card */}
                                    <div className="absolute inset-0 rounded-xl bg-[#22c55e] flex items-center justify-center"
                                        style={{ backfaceVisibility: 'hidden' }}>
                                        <Leaf weight="fill" className="text-white" size={32} />
                                    </div>
                                    {/* Front of card */}
                                    <div className={`absolute inset-0 rounded-xl overflow-hidden flex flex-col items-center justify-center ${isMatched ? 'ring-2 ring-[#22c55e]' : ''}`}
                                        style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                                        {card.image_url ? (
                                            <img src={card.image_url} alt={card.name} crossOrigin="anonymous" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-3xl">{card.emoji}</span>
                                        )}
                                        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs font-bold py-1 text-center">{card.name}</span>
                                    </div>
                                </motion.div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Animal fact modal */}
            <AnimatePresence>
                {matchedAnimal && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeAnimalModal}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="brutalist-card p-6 max-w-lg w-full bg-white"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="animal-fact-modal"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    {matchedAnimal.image_url ? (
                                        <img src={matchedAnimal.image_url} alt={matchedAnimal.name} crossOrigin="anonymous" referrerPolicy="no-referrer" className="w-14 h-14 rounded-xl object-cover border-2 border-[#1e293b]" />
                                    ) : (
                                        <span className="text-4xl">{matchedAnimal.emoji}</span>
                                    )}
                                    <div>
                                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b]">{matchedAnimal.name}</h3>
                                        <p className="text-xs text-[#22c55e] font-bold">Match Found!</p>
                                    </div>
                                </div>
                                <button onClick={closeAnimalModal} className="p-1 hover:bg-[#f1f5f9] rounded-lg" data-testid="close-fact-modal">
                                    <X weight="bold" size={20} className="text-[#94a3b8]" />
                                </button>
                            </div>

                            {matchedAnimal.image_url && (
                                <div className="rounded-xl overflow-hidden border-3 border-[#1e293b] mb-4">
                                    <img src={matchedAnimal.image_url} alt={matchedAnimal.name} crossOrigin="anonymous" referrerPolicy="no-referrer" className="w-full h-48 object-cover" />
                                </div>
                            )}

                            <p className="text-[#475569] mb-4">{matchedAnimal.fact}</p>

                            {matchedAnimal.video && matchedAnimal.video.length > 5 && (
                                <div className="aspect-video rounded-xl overflow-hidden border-3 border-[#1e293b] mb-4">
                                    <iframe
                                        src={matchedAnimal.video}
                                        title={`${matchedAnimal.name} video`}
                                        className="w-full h-full"
                                        allowFullScreen
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    />
                                </div>
                            )}

                            <button onClick={closeAnimalModal}
                                className="brutalist-btn w-full bg-[#22c55e] text-white" data-testid="continue-playing">
                                {matched.length === DIFFICULTIES[difficulty]?.pairs ? 'See Results!' : 'Keep Playing!'}
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
